"use client";

import React from "react";
import { Calendar, Tag, User, CircleAlert, ChevronLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="space-y-4">
          <div className="h-10 w-3/4 rounded-lg bg-muted"></div>
          <div className="h-10 w-1/2 rounded-lg bg-muted"></div>
        </div>
        <div className="flex gap-4">
          <div className="h-4 w-24 rounded bg-muted"></div>
          <div className="h-4 w-24 rounded bg-muted"></div>
        </div>
        <div className="aspect-video w-full rounded-xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-5/6 rounded bg-muted"></div>
          <div className="h-4 w-full rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CircleAlert className="mb-4 size-12 text-destructive" />
        <h2 className="mb-2 font-default text-2xl font-bold text-foreground">Error loading article</h2>
        <p className="text-muted-foreground">We couldn't load the studio insights. Please try again later.</p>
        <Button variant="outline" className="mt-6" onClick={() => window.location.reload()}>
          Reload Page
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <h2 className="mb-2 font-default text-2xl font-bold text-foreground">Article not found</h2>
        <p className="text-muted-foreground">The production story you're looking for doesn't exist or has been moved.</p>
        <Link to="/HomePage" className="mt-6">
          <Button>
            <ChevronLeft className="mr-2 size-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="mx-auto max-w-3xl">
      {}
      <header className="mb-8 space-y-6">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {post.categories.map((category, i) => (
              <Link
                key={category.id}
                to={category.link || "#"}
                data-index={i}
                className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
              >
                {category.name}
              </Link>
            ))}
          </div>
        )}

        <h1 className="font-default text-3xl font-bold leading-tight tracking-tighter text-foreground md:text-4xl lg:text-5xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-6 border border-border">
                <AvatarImage src={post.author.avatar?.["24"]} alt={post.author.name} />
                <AvatarFallback className="bg-muted text-[10px]">
                  <User className="size-3" />
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}

          {formattedDate && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>{formattedDate}</time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-10 overflow-hidden rounded-2xl bg-muted shadow-xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover"
            loading="eager"
          />
        </figure>
      )}

      {}
      <div
        className="prose prose-lg dark:prose-invert max-w-none text-foreground/90 font-default
          [&>p]:mb-6 [&>p]:leading-relaxed
          [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:font-default [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-foreground [&>h2]:tracking-tight
          [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:font-default [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-foreground
          [&>ul]:mb-6 [&>ul]:ml-6 [&>ul]:list-disc [&>ul>li]:mb-2
          [&>ol]:mb-6 [&>ol]:ml-6 [&>ol]:list-decimal [&>ol>li]:mb-2
          [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:bg-accent/30 [&>blockquote]:py-2
          [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80
          [&>img]:rounded-2xl [&>img]:shadow-lg [&>img]:my-8"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-10 flex flex-wrap items-center gap-2">
          <Tag className="mr-2 size-4 text-muted-foreground" />
          {post.tags.map((tag, i) => (
            <Link
              key={tag.id}
              to={tag.link || "#"}
              data-index={i}
              className="rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground hover:bg-secondary/80 transition-colors"
            >
              {tag.name}
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-10 bg-border/50" />

      {}
      {post.author && (
        <Card className="overflow-hidden border-none bg-card shadow-md rounded-2xl">
          <CardContent className="flex flex-col items-center gap-6 p-6 sm:flex-row sm:items-start">
            <Avatar className="size-20 border-2 border-background shadow-lg">
              <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
              <AvatarFallback className="bg-secondary text-2xl text-secondary-foreground font-default">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left">
              <h3 className="mb-1 font-default text-xl font-bold text-foreground">
                Produced by {post.author.name}
              </h3>
              <p className="mb-4 text-sm text-muted-foreground font-default">
                Senior Engineer at PulseWave Studios. Dedicated to capturing the raw energy of sound and translating artistic vision into sonic reality.
              </p>
              {post.author.link && (
                <Link to={post.author.link}>
                  <Button variant="outline" size="sm" className="rounded-full">
                    View Production Portfolio
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== "undefined" && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" };
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="223961"
      id="single-post-template-section"
      className="bg-background py-12 md:py-16 lg:py-24"
    >
      <div className="container mx-auto px-4 md:px-6">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}